import { useRef, useState, useCallback } from "react";
import AuthImage from "../AuthImage";
import { ArrowsLeftRight } from "@phosphor-icons/react";

/** Before/after wipe — the before photo sits on top, clipped at the divider; drag (or arrow keys) to reveal the after photo. */
export default function SwipeCompare({ before, after, beforeLabel = "Before", afterLabel = "After" }) {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);
  const update = useCallback((clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || !r.width) return;
    setPos(Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100)));
  }, []);
  const onDown = (e) => { dragging.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); update(e.clientX); };
  const onMove = (e) => { if (dragging.current) update(e.clientX); };
  const onUp = () => { dragging.current = false; };
  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); setPos((p) => Math.max(0, p - 5)); }
    if (e.key === "ArrowRight") { e.preventDefault(); setPos((p) => Math.min(100, p + 5)); }
  };
  return (
    <div ref={ref} role="slider" tabIndex={0} aria-label="Wipe between before and after" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pos)}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onKeyDown={onKey}
      className="relative aspect-[4/3] w-full max-h-[68vh] cursor-ew-resize select-none overflow-hidden rounded-md border border-border bg-slate-950 touch-none outline-none focus-visible:ring-2 focus-visible:ring-primary"
      data-testid="swipe-compare" data-pos={Math.round(pos)}>
      <AuthImage path={after.url} alt={after.name} className="pointer-events-none absolute inset-0 h-full w-full object-contain" draggable={false} />
      <div className="pointer-events-none absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <AuthImage path={before.url} alt={before.name} className="absolute inset-0 h-full w-full object-contain" draggable={false} />
      </div>
      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(0,0,0,.45)]" />
        <div className="absolute top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-800 shadow-lg ring-2 ring-primary/70" data-testid="swipe-handle"><ArrowsLeftRight size={18} weight="bold" /></div>
      </div>
      <span className="pointer-events-none absolute left-2 top-2 rounded bg-red-600/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">{beforeLabel}</span>
      <span className="pointer-events-none absolute right-2 top-2 rounded bg-emerald-600/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">{afterLabel}</span>
    </div>
  );
}
